import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

interface StoredRoute {
  handle: DetachedRouteHandle;
  token: string | null;
}

@Injectable()
export class EposRouteReuseStrategy implements RouteReuseStrategy {
  private storedRoutes = new Map<string, StoredRoute>();

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.data?.['reuse'] === true;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.getRouteKey(route);

    if (key) {
      this.storedRoutes.set(key, {
        handle,
        token: this.getCurrentToken(),
      });
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);
    if (!key) return false;

    const stored = this.storedRoutes.get(key);
    if (!stored) return false;

    // Never restore an EPOS screen that belongs to another login session.
    if (stored.token !== this.getCurrentToken()) {
      this.storedRoutes.delete(key);
      return false;
    }

    return true;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    return key ? this.storedRoutes.get(key)?.handle ?? null : null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    current: ActivatedRouteSnapshot,
  ): boolean {
    return future.routeConfig === current.routeConfig;
  }

  private getRouteKey(route: ActivatedRouteSnapshot): string | null {
    return route.routeConfig?.path ?? null;
  }

  private getCurrentToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return localStorage.getItem('token');
  }
}
